// Browser adapter — used when the desktop UI runs outside Tauri (PWA mode).
//
// Storage layout in localStorage:
//   "khal:enterprise:<serverUrl>" → JSON { serverUrl, wsUrl, token, version }
//   "khal:enterprise:_lastUsed"   → the serverUrl most recently written
//
// Multiple kernels can coexist side-by-side. `getEnterpriseConfig()` returns
// the most-recently-used entry, tracked via the `_lastUsed` metadata key.
//
// This module is the ONLY file in the UI allowed to touch `localStorage`.

import {
	clearAllKhalStorage,
	orgIdFromServerUrl,
	purgeKernelScopedStorage,
} from '@khal-os/desktop-shell';
import type { DesktopAdapter, EnterpriseConfig } from './index';

const KEY_PREFIX = 'khal:enterprise:';
const LAST_USED_KEY = 'khal:enterprise:_lastUsed';

function keyFor(serverUrl: string): string {
	return `khal:enterprise:${serverUrl}`;
}

/**
 * Convert an http(s) kernel URL to the ws(s) endpoint served by the kernel's
 * WS bridge. The kernel exposes NATS under `/ws/nats` (same path the Tauri
 * Rust relay uses — see `repos/core/tauri/src/ws_relay.rs`). We append it
 * only if the caller hasn't already pinned a path segment.
 */
function deriveWsUrl(serverUrl: string): string {
	let scheme: 'ws' | 'wss';
	let rest: string;
	if (serverUrl.startsWith('https://')) {
		scheme = 'wss';
		rest = serverUrl.slice('https://'.length);
	} else if (serverUrl.startsWith('http://')) {
		scheme = 'ws';
		rest = serverUrl.slice('http://'.length);
	} else {
		return serverUrl;
	}

	// Strip a single trailing slash so we don't produce `//ws/nats`.
	rest = rest.replace(/\/$/, '');

	// If the caller already included a path, respect it. Otherwise mount /ws/nats.
	const firstSlash = rest.indexOf('/');
	if (firstSlash === -1) {
		return `${scheme}://${rest}/ws/nats`;
	}
	return `${scheme}://${rest}`;
}

export const browserAdapter: DesktopAdapter = {
	async getEnterpriseConfig(): Promise<EnterpriseConfig | null> {
		if (typeof window === 'undefined') return null;
		const lastUsed = window.localStorage.getItem(LAST_USED_KEY);
		if (lastUsed) {
			const raw = window.localStorage.getItem(keyFor(lastUsed));
			if (raw) {
				try {
					return JSON.parse(raw) as EnterpriseConfig;
				} catch {
					// fall through to scan
				}
			}
		}
		// Fallback: scan all keys, pick any enterprise entry.
		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i);
			if (!key || !key.startsWith(KEY_PREFIX) || key === LAST_USED_KEY) continue;
			const raw = window.localStorage.getItem(key);
			if (!raw) continue;
			try {
				return JSON.parse(raw) as EnterpriseConfig;
			} catch {
				continue;
			}
		}
		return null;
	},

	async setEnterpriseConfig(serverUrl: string, token: string): Promise<void> {
		if (typeof window === 'undefined') return;
		const config: EnterpriseConfig = {
			serverUrl,
			wsUrl: deriveWsUrl(serverUrl),
			token,
			version: '',
		};
		window.localStorage.setItem(keyFor(serverUrl), JSON.stringify(config));
		window.localStorage.setItem(LAST_USED_KEY, serverUrl);
		// Primary guard for PWA tenant isolation: evict every other kernel's
		// scoped zustand state so windows/tabs from a previous kernel cannot
		// bleed into this one. The per-org store name is a defense-in-depth
		// second guard; see `packages/desktop-shell/src/lib/kernel-state.ts`.
		purgeKernelScopedStorage(orgIdFromServerUrl(serverUrl));
	},

	async exchangeAuthCode(serverUrl: string, code: string): Promise<{ token: string }> {
		const response = await fetch(`${serverUrl}/auth/exchange`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ code }),
			credentials: 'include',
		});
		if (!response.ok) {
			throw new Error(`exchangeAuthCode failed: ${response.status} ${response.statusText}`);
		}
		const body = (await response.json()) as { token?: string };
		if (!body.token) {
			throw new Error('exchangeAuthCode: response missing token');
		}
		await browserAdapter.setEnterpriseConfig(serverUrl, body.token);
		return { token: body.token };
	},

	async switchAccount(): Promise<void> {
		// No native "switch account" flow in PWA mode — clear the last-used
		// pointer so the next launch presents the kernel picker.
		if (typeof window === 'undefined') return;
		window.localStorage.removeItem(LAST_USED_KEY);
	},

	async clearEnterpriseConfig(): Promise<void> {
		// Purge every entry under the `khal` namespace — `khal:enterprise:*`
		// (kernel configs + _lastUsed pointer), `khal-windows:*` /
		// `khal-desktop-tabs:*` (zustand per-org stores), `khal-os:*`
		// (keybinds, recent apps), and `khal-theme-mode`. Widened from the
		// original `khal:enterprise:*`-only scope so fatal auth failures and
		// explicit sign-outs can't leave tenant-scoped UI state behind for
		// the next kernel to inherit. Anything outside the khal- / khal:
		// namespace is left alone.
		clearAllKhalStorage();
	},

	async showLauncher(): Promise<void> {
		// PWA has no separate launcher window; navigation is handled by the
		// caller. This is a no-op placeholder so the adapter contract holds.
	},
};
